import styled from 'styled-components';
import { FlexColumn, FlexRow } from '@make-software/cspr-design';
import { BodyText, Tooltip } from '@block-explorer/ui';
import { AccountInfoIcon } from '../../account-info-icon/account-info-icon';
import { ACCOUNT_DETAILS_PATH, SEARCH_PATH } from '../../../router/paths';
import React from 'react';
import { SHORTENED_ENTRY_TYPE_LENGTH, truncateName } from '../utils';
import { AccountInfoResult, Deploy } from '@block-explorer/entities';
import { useAccountInfoContext } from '../../../services/account-data-service';
import { buildRouterPath } from '../../../router/utils';
import { nftTokenMessage } from '../../../services/deploy-action-messages';
import { DefaultActionMessage } from './default-action-message';
import { deriveAccountInfo } from '../../../utils/account.tsx';
import { ContractIdentifier } from '../../contract-identifier/contract-identifier';
import { WasmProxyBadge } from '../wasm-proxy-badge.tsx';
import { isWASMProxyTransaction } from '../../../utils/contract.tsx';
import { useTranslation } from 'react-i18next';

const EntryTypeRow = styled(FlexRow)(() => ({
  maxWidth: '25rem',
}));
export const NftTokenActionMessage = ({ deploy }: { deploy: Deploy }) => {
  const { t } = useTranslation();
  const { getPublicKeyAccountHash, getAccountInfo } = useAccountInfoContext();
  const message = nftTokenMessage().prepareActionMessageData(
    deploy,
    getPublicKeyAccountHash,
    t
  );

  if (!message) {
    return <DefaultActionMessage deploy={deploy} />;
  }

  const account1ContractHash = message.account1?.hash;
  const account1Hash = message.account1?.accountHash;
  const account1PublicKey = message.account1?.publicKey;

  const publicKey1WithFallbackToHash = account1PublicKey || account1Hash;
  const fromApiAccountInfo = getAccountInfo<AccountInfoResult>(
    account1PublicKey || ''
  );

  const fromApiAccountInfoDetails = deriveAccountInfo(
    fromApiAccountInfo?.account_info ||
      fromApiAccountInfo?.centralized_account_info
  );

  const callerPublicKey = message.callerAccount?.publicKey;
  const callerAccountInfo = getAccountInfo<AccountInfoResult>(
    callerPublicKey || ''
  );

  const callerAccountInfoDetails = deriveAccountInfo(
    callerAccountInfo?.account_info ||
      callerAccountInfo?.centralized_account_info ||
      deploy.accountInfo
  );

  const path1 =
    (publicKey1WithFallbackToHash &&
      buildRouterPath(
        account1ContractHash ? SEARCH_PATH : ACCOUNT_DETAILS_PATH,
        { id: publicKey1WithFallbackToHash },
        undefined
      )) ||
    '';

  return (
    <FlexColumn>
      <EntryTypeRow align={'baseline'} itemsSpacing={4}>
        {isWASMProxyTransaction(deploy.executionTypeId) && <WasmProxyBadge />}
        <FlexRow itemsSpacing={6} align={'baseline'}>
          <Tooltip tooltipContent={message.entryPointName}>
            <BodyText monotype size={3} wordBreak noWrap>
              {truncateName(message.actionName, SHORTENED_ENTRY_TYPE_LENGTH)}
            </BodyText>
          </Tooltip>
          {message.numOfTokens && (
            <>
              <BodyText monotype size={3} wordBreak noWrap>
                {message.numOfTokens}
              </BodyText>
              <BodyText size={3} variation={'darkGray'}>{`NFT${
                message.numOfTokens > 1 ? '(s)' : ''
              }`}</BodyText>
            </>
          )}
        </FlexRow>
        <BodyText size={3} variation={'darkGray'}>
          <FlexRow itemsSpacing={6} align={'center'}>
            {message.callerAccount && message.callerAccount.publicKey && (
              <>
                {message.callerPrefix && <span>{message.callerPrefix}</span>}
                <AccountInfoIcon
                  publicKey={callerPublicKey}
                  logo={callerAccountInfoDetails?.logo}
                  csprName={callerAccountInfo?.cspr_name}
                  path={buildRouterPath(
                    ACCOUNT_DETAILS_PATH,
                    { id: callerPublicKey },
                    undefined
                  )}
                />
              </>
            )}
            {message.account1 && publicKey1WithFallbackToHash && (
              <>
                {message.prefix1 && <span>{message.prefix1}</span>}
                <AccountInfoIcon
                  publicKey={publicKey1WithFallbackToHash}
                  logo={fromApiAccountInfoDetails?.logo}
                  csprName={fromApiAccountInfo?.cspr_name}
                  path={path1}
                />
              </>
            )}
            {message.account1 && account1ContractHash && (
              <>
                {message.prefix1 && <span>{message.prefix1}</span>}
                <AccountInfoIcon
                  publicKey={account1ContractHash}
                  logo={null}
                  path={buildRouterPath(
                    SEARCH_PATH,
                    { id: account1ContractHash },
                    undefined
                  )}
                />
              </>
            )}
          </FlexRow>
        </BodyText>{' '}
      </EntryTypeRow>
      <FlexRow itemsSpacing={4}>
        <BodyText variation={'darkGray'} size={3}>
          with
        </BodyText>
        <ContractIdentifier
          hash={deploy.contractPackage.contract_package_hash}
          contractPackage={deploy.contractPackage}
        />
      </FlexRow>
    </FlexColumn>
  );
};
